import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useBoardLayout } from "@/hooks/useBoardLayout";
import { defaultLayout, findSpace, specFor, validateLayout, type Board, type SavedLayout } from "@shared/dashboardLayout";

/**
 * Edit-mode state for one dashboard tab. Changes live in a local draft until
 * Done (or leaving the page mid-edit, which also saves: there is nothing to
 * lose by keeping them). Every change is validated first, so a drag that would
 * push the layout past one screen is reverted instead of kept.
 */
export function useBoard(board: Board, canViewFinancials: boolean) {
  const { layout: saved, isLoading, save } = useBoardLayout(board);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<SavedLayout | null>(null);
  const [gridKey, setGridKey] = useState(0);
  const layout = draft ?? saved ?? defaultLayout(board);

  const pending = useRef<SavedLayout | null>(null);
  pending.current = editing ? draft : null;
  useEffect(() => () => { if (pending.current) save.mutate(pending.current); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const change = (next: SavedLayout) => {
    const ok = validateLayout(next, board, { canViewFinancials });
    if (!ok) {
      toast.error("That doesn't fit on one screen. Make something smaller first.");
      setGridKey((k) => k + 1); // remount the grid so it snaps back to the last good layout
      return;
    }
    setDraft(ok);
  };

  const add = (id: string) => {
    const spec = specFor(board, id);
    if (!spec) return;
    const spot = findSpace(layout.items, spec.size.w, spec.size.h);
    if (!spot) {
      toast.error("No room for that widget. Shrink or remove a widget first.");
      return;
    }
    change({ v: 1, items: [...layout.items, { id, ...spot, w: spec.size.w, h: spec.size.h }] });
  };

  const done = () => {
    if (draft) {
      save.mutate(draft, {
        onSuccess: () => setDraft(null),
        onError: () => toast.error("Couldn't save your layout"), // keep the draft so the edits are not lost
      });
    }
    setEditing(false);
  };

  const reset = () => {
    const d = defaultLayout(board);
    setDraft(d);
    save.mutate(d, { onError: () => toast.error("Couldn't reset your layout") });
  };

  return { layout, ready: !isLoading, editing, gridKey, startEditing: () => setEditing(true), done, reset, change, add };
}
